import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearUserSession } from '../utils/userAuth';

interface InformationAuthContextType {
    informationData: any | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    logout: () => void;
    updateInformationData: (updatedInformation: any) => void;
    user: any | null;
}

const InformationAuthContext = createContext<InformationAuthContextType | undefined>(undefined);

export const useInformationAuth = () => {
    const context = useContext(InformationAuthContext);
    if (!context) {
        throw new Error('useInformationAuth must be used within an InformationAuthProvider');
    }
    return context;
};

interface InformationAuthProviderProps {
    children: ReactNode;
}

export const InformationAuthProvider: React.FC<InformationAuthProviderProps> = ({ children }) => {
    const [informationData, setInformationData] = useState<any | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const handleForcedLogout = useCallback(() => {
        clearUserSession();
        setToken(null);
        setInformationData(null);
        navigate('/user/login');
    }, [navigate]);

    // Initialize auth state from localStorage
    useEffect(() => {
        const initAuth = async () => {
            const storedToken = localStorage.getItem('userToken');
            const storedInformation = localStorage.getItem('informationInfo');
            const storedUserInfo = localStorage.getItem('userInfo');

            if (storedToken) {
                setToken(storedToken);
            }

            if (storedInformation) {
                try {
                    const informationDataParsed = JSON.parse(storedInformation);
                    setInformationData(informationDataParsed);
                } catch (error) {
                    clearUserSession();
                }
            } else if (storedUserInfo) {
                try {
                    const userDataParsed = JSON.parse(storedUserInfo);
                    setInformationData({
                        email: userDataParsed?.email,
                        requires_password_change: userDataParsed?.requires_password_change ?? false,
                    });
                } catch (error) {
                    // Ignore parse errors
                }
            }

            setIsLoading(false);
        };

        initAuth();
    }, []);

    // Listen for storage changes (e.g., password change on login page)
    useEffect(() => {
        const handleStorageChange = () => {
            const storedToken = localStorage.getItem('userToken');
            const storedInformation = localStorage.getItem('informationInfo');
            const storedUserInfo = localStorage.getItem('userInfo');

            if (storedToken) {
                setToken(storedToken);
            }

            if (storedInformation) {
                try {
                    const informationDataParsed = JSON.parse(storedInformation);
                    setInformationData(informationDataParsed);
                } catch (error) {
                    // Ignore parse errors
                }
            } else if (storedUserInfo) {
                try {
                    const userDataParsed = JSON.parse(storedUserInfo);
                    setInformationData({
                        email: userDataParsed?.email,
                        requires_password_change: userDataParsed?.requires_password_change ?? false,
                    });
                } catch (error) {
                    // Ignore parse errors
                }
            }
        };

        // Listen for browser storage events (cross-tab)
        window.addEventListener('storage', handleStorageChange);

        // Listen for custom auth update events (same-tab)
        window.addEventListener('authStorageUpdated', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('authStorageUpdated', handleStorageChange);
        };
    }, []);

    const logout = useCallback(() => {
        handleForcedLogout();
    }, [handleForcedLogout]);

    const updateInformationData = (updatedInformation: any) => {
        setInformationData(updatedInformation);
        localStorage.setItem('informationInfo', JSON.stringify(updatedInformation));
    };

    const isAuthenticated = !!token;

    return (
        <InformationAuthContext.Provider
            value={{
                informationData,
                token,
                isAuthenticated,
                isLoading,
                logout,
                updateInformationData,
                user: null,
            }}
        >
            {children}
        </InformationAuthContext.Provider>
    );
};
